import React from "react";

/**
 * True square alignment guide centered on the viewfinder (not stretched to video aspect).
 * TL/BL on the left; dashed void top-right where the nested QR sits.
 *
 * Hint boxes are drawn in module space scaled to the 0-100 viewBox, so they track
 * whatever outer matrix the server reports rather than assuming a QR version.
 */
const FINDER_MODULES = 7;
const VIEWBOX = 100;

export default function MatrixGuideOverlay({ locked = false, grid, missingModules, symbol = "qr" }) {
  const stroke = locked ? "#16a34a" : "#FFCC00";
  const label = locked ? "#14532d" : "#111";

  const pct = (modules) => (modules / grid) * VIEWBOX;
  const finder = pct(FINDER_MODULES);
  const inset = pct(1);
  const voidCols = missingModules?.cols ?? 0;
  const voidRows = missingModules?.rows ?? 0;
  const voidX = pct(missingModules?.col0 ?? grid - voidCols);
  const voidY = pct(missingModules?.row0 ?? 0);

  return (
    <div className="absolute inset-0 pointer-events-none flex items-center justify-center p-[7%]">
      {/* dimmer with square hole via box-shadow */}
      <div
        className="relative aspect-square w-full max-h-full"
        style={{
          boxShadow: "0 0 0 9999px rgba(0,0,0,0.4)",
        }}
      >
        <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet">
          <rect
            x="1"
            y="1"
            width="98"
            height="98"
            fill="none"
            stroke={stroke}
            strokeWidth="2.2"
          />
          {symbol === "datamatrix" ? (
            <>
              <polyline
                points={`${inset},${inset} ${inset},${VIEWBOX - inset} ${VIEWBOX - inset},${VIEWBOX - inset}`}
                fill="none"
                stroke={stroke}
                strokeWidth="2.4"
                strokeLinejoin="miter"
              />
              <text x={inset + 2} y={VIEWBOX - inset - 2} fill={label} fontSize="5" fontFamily="ui-monospace, monospace" fontWeight="700">
                L
              </text>
            </>
          ) : (
            <>
              <rect
                x={inset}
                y={inset}
                width={finder}
                height={finder}
                fill="none"
                stroke={stroke}
                strokeWidth="1.4"
                strokeDasharray="3 2"
              />
              <circle cx={inset + finder / 2} cy={inset + finder / 2} r="2" fill={stroke} />
              <text x={inset} y={inset + finder + 6} fill={label} fontSize="5" fontFamily="ui-monospace, monospace" fontWeight="700">
                TL
              </text>
              <rect
                x={inset}
                y={VIEWBOX - inset - finder}
                width={finder}
                height={finder}
                fill="none"
                stroke={stroke}
                strokeWidth="1.4"
                strokeDasharray="3 2"
              />
              <circle cx={inset + finder / 2} cy={VIEWBOX - inset - finder / 2} r="2" fill={stroke} />
              <text x={inset} y={VIEWBOX - inset - finder - 2} fill={label} fontSize="5" fontFamily="ui-monospace, monospace" fontWeight="700">
                BL
              </text>
            </>
          )}
          {/* void / nested */}
          <rect
            x={voidX}
            y={voidY}
            width={pct(voidCols)}
            height={pct(voidRows)}
            fill={locked ? "rgba(22,163,74,0.15)" : "rgba(255,204,0,0.18)"}
            stroke={stroke}
            strokeWidth="1.4"
            strokeDasharray="2 2"
          />
          <text x={voidX} y={voidY + pct(voidRows) + 5} fill={label} fontSize="4.5" fontFamily="ui-monospace, monospace" fontWeight="700">
            void
          </text>
        </svg>
      </div>
    </div>
  );
}
